/**
 * useComplianceFilterDimensions — backend-driven filter dimensions for the compliance browser.
 *
 * DRR-055/074: The frontend never hardcodes filter values (no industry lists,
 * no jurisdiction maps, no safety level arrays). All filter chips are built
 * from the backend-returned dimensions.
 *
 * Returns:
 *   - `dimensions` — array of {dimension, label, renderAs, isMultiSelect, values[]}
 *   - `totalCount`  — total ruleset count before any filter applied
 *
 * Each `value` entry is: { value: "MN", label: "Minnesota", count: 12 }
 * The `value` field maps directly to `ruleSetsByDimensions` filter input fields.
 */

import {
  useComplianceFilterDimensionsQuery,
} from '@/generated/graphql';
export interface ComplianceFilterValue {
  __typename?: string;
  value: string;
  label: string;
  count: number;
}

export interface ComplianceFilterDimension {
  __typename?: string;
  dimension: string;
  label: string;
  renderAs: string;
  isMultiSelect: boolean;
  values: ComplianceFilterValue[];
}

export interface UseComplianceFilterDimensionsReturn {
  dimensions: ComplianceFilterDimension[];
  totalCount: number;
  loading: boolean;
  error: Error | undefined;
}

export function useComplianceFilterDimensions(): UseComplianceFilterDimensionsReturn {
  const { data, loading, error } = useComplianceFilterDimensionsQuery({
    fetchPolicy: 'cache-and-network',
  });

  return {
    dimensions: data?.complianceFilterDimensions?.dimensions ?? [],
    totalCount: data?.complianceFilterDimensions?.totalCount ?? 0,
    loading,
    error: error as Error | undefined,
  };
}
